import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { 
  Gamepad2, 
  User, 
  LogOut, 
  Menu, 
  X,
  BarChart3,
  Shield
} from 'lucide-react';

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { admin, isAuthenticated: isAdminAuthenticated, logout: adminLogout } = useAdminAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    if (isAdminAuthenticated) {
      adminLogout();
    } else {
      logout();
    }
    navigate('/');
  };

  const getSubscriptionBadge = (type) => {
    switch (type) {
      case 'premium':
        return <span className="badge badge-primary">Premium</span>;
      case 'pro':
        return <span className="badge badge-success">Pro</span>;
      case 'server':
        return <span className="badge badge-warning">Server</span>;
      default:
        return <span className="badge">Free</span>;
    }
  };

  return (
    <nav className="bg-discord-darker border-b border-discord-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <Gamepad2 className="h-8 w-8 text-discord-blurple" />
              <span className="text-xl font-bold text-discord-lightest">
                Discord Game Bot
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {isAdminAuthenticated ? (
              <>
                <Link
                  to="/admin"
                  className="text-discord-light hover:text-discord-lightest px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Admin Dashboard
                </Link>
                
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-discord-blurple" />
                  <span className="badge badge-warning">Admin</span>
                  
                  <div className="flex items-center space-x-2">
                    <div className="h-8 w-8 rounded-full bg-discord-blurple flex items-center justify-center">
                      <Shield className="h-4 w-4 text-white" />
                    </div>
                    
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-discord-lightest">
                        {admin?.username}
                      </span>
                      <span className="text-xs text-discord-light">
                        {admin?.role}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handleLogout}
                    className="p-2 text-discord-light hover:text-discord-lightest rounded-md transition-colors"
                    title="Logout"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </>
            ) : isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  className="text-discord-light hover:text-discord-lightest px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Dashboard
                </Link>
                <Link
                  to="/games"
                  className="text-discord-light hover:text-discord-lightest px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Games
                </Link>
                <Link
                  to="/leaderboard"
                  className="text-discord-light hover:text-discord-lightest px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Leaderboard
                </Link>
                
                {user?.subscription?.type === 'server' && (
                  <Link
                    to="/analytics"
                    className="text-discord-light hover:text-discord-lightest px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-1"
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span>Analytics</span>
                  </Link>
                )}

                <div className="flex items-center space-x-3">
                  {getSubscriptionBadge(user?.subscription?.type)}
                  
                  <div className="flex items-center space-x-2">
                    {user?.avatar ? (
                      <img
                        src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png`}
                        alt={user.username}
                        className="h-8 w-8 rounded-full"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-discord-blurple flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                      </div>
                    )}
                    
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-discord-lightest">
                        {user?.username}
                      </span>
                      <span className="text-xs text-discord-light">
                        #{user?.discriminator}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Link
                      to="/profile"
                      className="p-2 text-discord-light hover:text-discord-lightest rounded-md transition-colors"
                    >
                      <User className="h-4 w-4" />
                    </Link>
                    
                    <button
                      onClick={handleLogout}
                      className="p-2 text-discord-light hover:text-discord-red rounded-md transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/leaderboard"
                  className="text-discord-light hover:text-discord-lightest px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Leaderboard
                </Link>
                
                <button
                  onClick={() => {
                    // This will trigger Discord OAuth
                    window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/discord/url`;
                  }}
                  className="btn btn-primary"
                >
                  Login with Discord
                </button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-discord-light hover:text-discord-lightest p-2 rounded-md"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-discord-darker border-t border-discord-dark">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {isAdminAuthenticated ? (
              <>
                <Link
                  to="/admin"
                  className="block px-3 py-2 text-discord-light hover:text-discord-lightest rounded-md text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Admin Dashboard
                </Link>
                
                <div className="px-3 py-2 border-t border-discord-dark">
                  <div className="flex items-center space-x-2 mb-2">
                    <Shield className="h-5 w-5 text-discord-blurple" />
                    <span className="text-sm font-medium text-discord-lightest">
                      {admin?.username}
                    </span>
                    <span className="badge badge-warning text-xs">{admin?.role}</span>
                  </div>
                  
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 text-discord-light hover:text-discord-lightest"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </>
            ) : isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  className="block px-3 py-2 text-discord-light hover:text-discord-lightest rounded-md text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/games"
                  className="block px-3 py-2 text-discord-light hover:text-discord-lightest rounded-md text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Games
                </Link>
                <Link
                  to="/leaderboard"
                  className="block px-3 py-2 text-discord-light hover:text-discord-lightest rounded-md text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Leaderboard
                </Link>
                <Link
                  to="/profile"
                  className="block px-3 py-2 text-discord-light hover:text-discord-lightest rounded-md text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile
                </Link>
                
                {user?.subscription?.type === 'server' && (
                  <Link
                    to="/analytics"
                    className="block px-3 py-2 text-discord-light hover:text-discord-lightest rounded-md text-base font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Analytics
                  </Link>
                )}

                <div className="border-t border-discord-dark pt-3 mt-3">
                  <div className="flex items-center space-x-3 px-3 py-2">
                    {user?.avatar ? (
                      <img
                        src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png`}
                        alt={user.username}
                        className="h-8 w-8 rounded-full"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-discord-blurple flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-discord-lightest">
                          {user?.username}
                        </span>
                        {getSubscriptionBadge(user?.subscription?.type)}
                      </div>
                      <span className="text-xs text-discord-light">
                        #{user?.discriminator}
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-discord-red hover:bg-discord-dark rounded-md text-base font-medium"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/leaderboard"
                  className="block px-3 py-2 text-discord-light hover:text-discord-lightest rounded-md text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Leaderboard
                </Link>
                
                <button
                  onClick={() => {
                    window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/discord/url`;
                  }}
                  className="w-full text-left px-3 py-2 btn btn-primary"
                >
                  Login with Discord
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
